import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { serviceReportsApi, customersApi, serialNumbersApi, usersApi } from "@/lib/api";
import { ActivityPanel } from "@/components/common/ActivityPanel";
import { FormStepper } from "@/components/common/FormStepper";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/SimpleToast";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Trash2,
  Loader2,
  Download,
  Paperclip,
  X,
  Check,
  Cloud,
  CloudOff,
} from "lucide-react";
import { AttachmentsField } from "@/components/AttachmentsField";
import { serviceReportAttachmentsApi } from "@/lib/api";
import { loadFormDraft, saveFormDraft, clearFormDraft } from "@/hooks/useFormDraft";
import { ServiceReportCustomerDevice } from "@/components/service-report/ServiceReportCustomerDevice";
import { ServiceReportWorkDescription } from "@/components/service-report/ServiceReportWorkDescription";
import { ServiceReportTimeMaterials } from "@/components/service-report/ServiceReportTimeMaterials";
import { ServiceReportSignatures } from "@/components/service-report/ServiceReportSignatures";

const DRAFT_KEY = "draft:service-report:new";

const TECHNICIANS = ["Oliver Rudolph", "Thomas Röll"];

const STEPS = [
  { key: "customer", label: "Kunde & Gerät" },
  { key: "work", label: "Arbeit" },
  { key: "time", label: "Zeit & Material" },
  { key: "finish", label: "Unterschrift & Anhänge" },
];

// Berechnet die Arbeitszeit (in Stunden) aus Uhrzeit von/bis ("HH:MM"). Gibt null zurück, wenn unvollständig.
const computeHoursFromTimes = (from, to, breakMins = 0) => {
  if (!from || !to) return null;
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  if ([fh, fm, th, tm].some((n) => Number.isNaN(n))) return null;
  const mins = th * 60 + tm - (fh * 60 + fm) - Math.max(0, parseInt(breakMins, 10) || 0);
  if (mins <= 0) return 0;
  return Math.round((mins / 60) * 100) / 100;
};

export default function ServiceReportForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get("customer");
  const { user } = useAuth();
  const toast = useToast();
  const isEditing = !!id;

  const [customers, setCustomers] = useState([]);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [customerId, setCustomerId] = useState("");
  const [reportUpdatedAt, setReportUpdatedAt] = useState(null);
  const [serialNumberId, setSerialNumberId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [workHours, setWorkHours] = useState("");
  const [travelHours, setTravelHours] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("");
  const [materials, setMaterials] = useState([]);
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [technicianName, setTechnicianName] = useState("");
  const [customerSignerName, setCustomerSignerName] = useState("");
  const [technicianSignature, setTechnicianSignature] = useState(null);
  const [customerSignature, setCustomerSignature] = useState(null);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [signedAt, setSignedAt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // Wizard
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);

  // Autosave-Status: "idle" | "saving" | "saved" | "offline"
  const [autosave, setAutosave] = useState("idle");
  const [autosaveTime, setAutosaveTime] = useState(null);

  const techSigRef = useRef(null);
  const custSigRef = useRef(null);

  const fetchSerialNumbers = useCallback(async (custId) => {
    if (!custId) {
      setSerialNumbers([]);
      return;
    }
    try {
      const response = await serialNumbersApi.getAll(null, custId);
      setSerialNumbers(response.data);
    } catch (error) {
      console.warn("Seriennummern konnten nicht geladen werden", error);
    }
  }, []);

  const applyDraft = useCallback(async (draft) => {
    if (!draft || typeof draft !== "object") return false;
    if (draft.customerId !== undefined) setCustomerId(draft.customerId);
    if (draft.serialNumberId !== undefined) setSerialNumberId(draft.serialNumberId);
    if (draft.title !== undefined) setTitle(draft.title);
    if (draft.description !== undefined) setDescription(draft.description);
    if (draft.startTime !== undefined) setStartTime(draft.startTime);
    if (draft.endTime !== undefined) setEndTime(draft.endTime);
    if (draft.workHours !== undefined) setWorkHours(draft.workHours);
    if (draft.travelHours !== undefined) setTravelHours(draft.travelHours);
    if (draft.breakMinutes !== undefined) setBreakMinutes(draft.breakMinutes);
    if (Array.isArray(draft.materials)) setMaterials(draft.materials);
    if (draft.serviceDate) setServiceDate(draft.serviceDate);
    if (draft.technicianName !== undefined) setTechnicianName(draft.technicianName);
    if (draft.customerSignerName !== undefined) setCustomerSignerName(draft.customerSignerName);
    if (draft.customerId) await fetchSerialNumbers(draft.customerId);
    return true;
  }, [fetchSerialNumbers]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const customersRes = await customersApi.getAllLite();
        setCustomers(customersRes.data);

        if (isEditing) {
          const reportRes = await serviceReportsApi.getById(id);
          const report = reportRes.data;
          setCustomerId(report.customer_id || "");
          setReportUpdatedAt(report.updated_at || null);
          setSerialNumberId(report.serial_number_id || "");
          setTitle(report.title);
          setDescription(report.description);
          setStartTime(report.start_time || "");
          setEndTime(report.end_time || "");
          setWorkHours(report.work_hours ?? "");
          setTravelHours(report.travel_hours ?? "");
          setBreakMinutes(report.break_minutes ? String(report.break_minutes) : "");
          setMaterials((report.materials || []).map((mat, idx) => ({
            ...mat,
            id: mat.id || `mat-loaded-${idx}-${Date.now()}`
          })));
          setServiceDate(report.service_date);
          setTechnicianName(report.technician_name);
          setCustomerSignerName(report.customer_signer_name || "");
          setTechnicianSignature(report.technician_signature || null);
          setCustomerSignature(report.customer_signature || null);
          setSignedAt(report.signed_at || null);
          setMaxReached(STEPS.length - 1);
          if (report.customer_id) {
            await fetchSerialNumbers(report.customer_id);
          }
        } else {
          setCustomerId(preselectedCustomer || "");
          // Techniker = eingeloggter Benutzer (falls in der Auswahlliste)
          if (user?.full_name && TECHNICIANS.includes(user.full_name)) {
            setTechnicianName(user.full_name);
          } else {
            setTechnicianName("");
          }
          // Unterschrift des eingeloggten Technikers automatisch vorausfüllen
          try {
            const sigRes = await usersApi.getMySignature();
            if (sigRes.data?.signature) {
              setTechnicianSignature(sigRes.data.signature);
            }
          } catch (e) {
            console.debug("Keine hinterlegte Unterschrift", e);
          }
          if (preselectedCustomer) {
            await fetchSerialNumbers(preselectedCustomer);
          }
          // Nicht abgeschickten Entwurf wiederherstellen: zuerst DB (gerätübergreifend),
          // sonst lokalen Entwurf (überlebt Offline/Reload).
          let restored = false;
          try {
            const dbRes = await serviceReportsApi.getDraft();
            if (dbRes.data?.data) {
              restored = await applyDraft(dbRes.data.data);
              if (dbRes.data.updated_at) setAutosaveTime(new Date(dbRes.data.updated_at));
            }
          } catch (e) {
            console.debug("DB-Entwurf nicht ladbar", e);
          }
          if (!restored) {
            const localDraft = loadFormDraft(DRAFT_KEY);
            if (localDraft) {
              if (localDraft.technicianSignature) setTechnicianSignature(localDraft.technicianSignature);
              if (localDraft.customerSignature) setCustomerSignature(localDraft.customerSignature);
              restored = await applyDraft(localDraft);
            }
          }
          if (restored) {
            setDraftRestored(true);
            setMaxReached(STEPS.length - 1);
          }
        }
      } catch (error) {
        navigate("/service-reports");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [id, isEditing, navigate, preselectedCustomer, fetchSerialNumbers, user, applyDraft]);

  // Handle keyboard visibility on mobile - must be before any conditional returns
  useEffect(() => {
    const handleResize = () => {
      const isKeyboardOpen = window.innerHeight < window.screen.height * 0.75;
      document.body.classList.toggle('keyboard-active', isKeyboardOpen);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Autosave (nur neue Berichte): in die DB speichern + lokalen Fallback schreiben.
  useEffect(() => {
    if (isEditing || loading) return;
    // Nichts speichern, wenn das Formular praktisch leer ist.
    const hasContent = customerId || title || description || materials.length > 0;
    if (!hasContent) return;

    const draft = {
      customerId,
      serialNumberId,
      title,
      description,
      startTime,
      endTime,
      workHours,
      travelHours,
      breakMinutes,
      materials,
      serviceDate,
      technicianName,
      customerSignerName,
    };
    // Lokaler Sofort-Fallback (inkl. Signaturen für Offline-Komfort)
    saveFormDraft(DRAFT_KEY, { ...draft, technicianSignature, customerSignature });

    setAutosave("saving");
    const t = setTimeout(async () => {
      try {
        const res = await serviceReportsApi.saveDraft(draft);
        setAutosave("saved");
        setAutosaveTime(res.data?.updated_at ? new Date(res.data.updated_at) : new Date());
      } catch (e) {
        setAutosave("offline");
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [
    isEditing, loading, customerId, serialNumberId, title, description,
    startTime, endTime, workHours, travelHours, breakMinutes, materials, serviceDate,
    technicianName, customerSignerName, technicianSignature, customerSignature,
  ]);

  const handleDiscardDraft = async () => {
    clearFormDraft(DRAFT_KEY);
    try { await serviceReportsApi.deleteDraft(); } catch (e) { console.debug("deleteDraft", e); }
    setCustomerId(preselectedCustomer || "");
    setSerialNumberId("");
    setTitle("");
    setDescription("");
    setStartTime("");
    setEndTime("");
    setWorkHours("");
    setTravelHours("");
    setBreakMinutes("");
    setMaterials([]);
    setServiceDate(new Date().toISOString().split("T")[0]);
    setCustomerSignerName("");
    setCustomerSignature(null);
    setDraftRestored(false);
    setStep(0);
    setMaxReached(0);
    setAutosave("idle");
    setAutosaveTime(null);
    toast.info("Entwurf verworfen");
  };

  const handleCustomerChange = (value) => {
    setCustomerId(value);
    setSerialNumberId("");
    fetchSerialNumbers(value);
  };

  // Validiert den angegebenen Schritt. Gibt Fehlertext oder null zurück.
  const validateStep = (idx) => {
    if (idx === 0 && !customerId) return "Bitte wählen Sie einen Kunden aus";
    if (idx === 1) {
      if (!title.trim()) return "Bitte geben Sie einen Grund an";
      if (!serviceDate) return "Bitte wählen Sie ein Datum";
      if (!description.trim()) return "Bitte beschreiben Sie die durchgeführten Arbeiten";
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      toast.error(err);
      return;
    }
    const next = Math.min(step + 1, STEPS.length - 1);
    setStep(next);
    setMaxReached((m) => Math.max(m, next));
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const goToStep = (idx) => {
    if (idx <= maxReached) setStep(idx);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== STEPS.length - 1) return; // Submit nur im letzten Schritt
    // Alle Pflichtschritte prüfen
    for (let i = 0; i < STEPS.length; i++) {
      const err = validateStep(i);
      if (err) {
        setStep(i);
        toast.error(err);
        return;
      }
    }

    setSaving(true);
    try {
      // State-Werte verwenden (via onSignatureChange gesetzt): nur bei echter
      // Nutzer-Aktion geändert. getSignature() würde das Canvas neu enkodieren und
      // dadurch bei unveränderter Unterschrift fälschlich eine Änderung erzeugen.
      const techSig = technicianSignature;
      const custSig = customerSignature;

      const timeHours = computeHoursFromTimes(startTime, endTime, breakMinutes);
      const effectiveWorkHours = timeHours !== null ? timeHours : (parseFloat(workHours) || 0);

      const data = {
        customer_id: customerId,
        serial_number_id: serialNumberId || null,
        title,
        description,
        start_time: startTime || null,
        end_time: endTime || null,
        work_hours: effectiveWorkHours,
        travel_hours: parseFloat(travelHours) || 0,
        break_minutes: Math.max(0, parseInt(breakMinutes, 10) || 0),
        materials: materials.map((m) => {
          const n = parseInt(m.quantity, 10);
          return { ...m, quantity: Number.isFinite(n) && n >= 1 ? n : 1 };
        }),
        service_date: serviceDate,
        technician_name: technicianName,
        customer_signer_name: customerSignerName,
        technician_signature: techSig,
        customer_signature: custSig,
        attachment_ids: attachmentIds,
      };

      if (isEditing) {
        const res = await serviceReportsApi.update(id, data, reportUpdatedAt);
        if (res?._queued) {
          toast.info("Offline gespeichert – wird synchronisiert, sobald wieder Verbindung besteht");
        } else {
          toast.success("Bericht aktualisiert");
        }
      } else {
        const res = await serviceReportsApi.create(data);
        if (res?._queued) {
          toast.info("Offline gespeichert – wird synchronisiert, sobald wieder Verbindung besteht");
        } else {
          toast.success("Bericht erstellt");
        }
        clearFormDraft(DRAFT_KEY);
        try { await serviceReportsApi.deleteDraft(); } catch (err) { console.debug("deleteDraft", err); }
      }
      navigate("/service-reports");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMaterial = () => {
    setMaterials([...materials, { id: `mat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, description: "", quantity: 1 }]);
  };

  const handleUpdateMaterial = (index, field, value) => {
    const updated = materials.map((mat, i) =>
      i === index ? { ...mat, [field]: value } : mat
    );
    setMaterials(updated);
  };

  const handleRemoveMaterial = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const handleDownloadPdf = async () => {
    if (!isEditing) return;
    setDownloading(true);
    try {
      const response = await serviceReportsApi.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `servicebericht-${id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim PDF-Download");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="spinner" />
      </div>
    );
  }

  const timeHours = computeHoursFromTimes(startTime, endTime, breakMinutes);
  const effectiveWorkHours = timeHours !== null ? timeHours : (parseFloat(workHours) || 0);
  const totalHours = Math.round((effectiveWorkHours + (parseFloat(travelHours) || 0)) * 100) / 100;
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="space-y-6 animate-fade-in pb-28 md:pb-6" data-testid="service-report-form">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/service-reports")} data-testid="back-btn" className="shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight truncate">
            {isEditing ? "Bericht bearbeiten" : "Neuer Servicebericht"}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">Erfassen Sie die Arbeitsdetails</p>
        </div>
        {!isEditing && <AutosaveIndicator state={autosave} time={autosaveTime} />}
        {isEditing && (
          <Button variant="outline" onClick={handleDownloadPdf} disabled={downloading} data-testid="download-pdf-btn" className="shrink-0">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin md:mr-2" /> : <Download className="w-4 h-4 md:mr-2" />}
            <span className="hidden md:inline">PDF</span>
          </Button>
        )}
      </div>

      {draftRestored && (
        <div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm"
          data-testid="draft-restored-banner"
        >
          <span className="text-amber-800 dark:text-amber-200">
            Ein nicht abgeschickter Entwurf wurde wiederhergestellt.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDiscardDraft}
            data-testid="discard-draft-btn"
            className="shrink-0"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Entwurf verwerfen
          </Button>
        </div>
      )}

      {/* Fortschritts-Stepper */}
      <Card>
        <CardContent className="py-4 px-4 md:px-6">
          <FormStepper steps={STEPS} current={step} maxReached={maxReached} onStepClick={goToStep} />
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        {step === 0 && (
          <ServiceReportCustomerDevice
            customers={customers}
            customerId={customerId}
            onCustomerChange={handleCustomerChange}
            serialNumbers={serialNumbers}
            serialNumberId={serialNumberId}
            onSerialNumberChange={setSerialNumberId}
          />
        )}

        {step === 1 && (
          <ServiceReportWorkDescription
            title={title}
            setTitle={setTitle}
            serviceDate={serviceDate}
            setServiceDate={setServiceDate}
            technicianName={technicianName}
            setTechnicianName={setTechnicianName}
            technicians={TECHNICIANS}
            description={description}
            setDescription={setDescription}
          />
        )}

        {step === 2 && (
          <ServiceReportTimeMaterials
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
            workHours={effectiveWorkHours}
            travelHours={travelHours}
            setTravelHours={setTravelHours}
            breakMinutes={breakMinutes}
            setBreakMinutes={setBreakMinutes}
            totalHours={totalHours}
            materials={materials}
            onAddMaterial={handleAddMaterial}
            onUpdateMaterial={handleUpdateMaterial}
            onRemoveMaterial={handleRemoveMaterial}
          />
        )}

        {step === 3 && (
          <>
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
                  <Paperclip className="w-5 h-5" />
                  Anhänge
                </CardTitle>
                <CardDescription className="text-sm">
                  Fotos vom Einsatz oder Dokumente (Drag &amp; Drop, max. 25 MB pro Datei)
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
                <AttachmentsField
                  ownerId={id}
                  api={serviceReportAttachmentsApi}
                  onIdsChange={setAttachmentIds}
                  testidPrefix="report-attachment"
                  label="Dateien"
                />
              </CardContent>
            </Card>

            <ServiceReportSignatures
              customerSignerName={customerSignerName}
              setCustomerSignerName={setCustomerSignerName}
              signedAt={signedAt}
              techSigRef={techSigRef}
              technicianSignature={technicianSignature}
              setTechnicianSignature={setTechnicianSignature}
              custSigRef={custSigRef}
              customerSignature={customerSignature}
              setCustomerSignature={setCustomerSignature}
            />
          </>
        )}

        {/* Desktop-Navigation */}
        <div className="hidden md:flex justify-between gap-4">
          <div>
            {step > 0 && (
              <Button type="button" variant="outline" onClick={goBack} data-testid="wizard-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="ghost" onClick={() => navigate("/service-reports")}>
              Abbrechen
            </Button>
            {!isLastStep ? (
              <Button key="wizard-next" type="button" onClick={goNext} data-testid="wizard-next-btn" className="bg-[#1A3A5C] hover:bg-[#142E4A]">
                Weiter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button key="wizard-save" type="submit" disabled={saving} data-testid="save-report-btn" className="bg-[#E8700A] hover:bg-[#C95F06]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {isEditing ? "Speichern" : "Erstellen"}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Sticky Footer */}
        <div className="fixed md:hidden bottom-[calc(68px+env(safe-area-inset-bottom))] left-0 right-0 bg-background border-t border-border p-3 flex gap-2 z-50 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          {step > 0 ? (
            <Button type="button" variant="outline" onClick={goBack} className="flex-1 h-12 text-base" data-testid="wizard-back-btn-mobile">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => navigate("/service-reports")} className="flex-1 h-12 text-base">
              <X className="w-4 h-4 mr-2" />
              Abbrechen
            </Button>
          )}
          {!isLastStep ? (
            <Button key="wizard-next-mobile" type="button" onClick={goNext} className="flex-1 h-12 text-base bg-[#1A3A5C] hover:bg-[#142E4A]" data-testid="wizard-next-btn-mobile">
              Weiter
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button key="wizard-save-mobile" type="submit" disabled={saving} data-testid="save-report-btn-mobile" className="flex-1 h-12 text-base bg-[#E8700A] hover:bg-[#C95F06]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {isEditing ? "Speichern" : "Erstellen"}
            </Button>
          )}
        </div>
      </form>

      {isEditing && (
        <div className="mt-8 pb-24 md:pb-8">
          <h2 className="text-lg font-semibold mb-3">Aktivität</h2>
          <ActivityPanel entityType="service_report" entityId={id} />
        </div>
      )}
    </div>
  );
}

// Dezenter Autosave-Indikator oben rechts.
function AutosaveIndicator({ state, time }) {
  if (state === "idle") return null;
  const timeLabel = time
    ? time.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : "";
  let content;
  if (state === "saving") {
    content = (
      <>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Wird gespeichert…</span>
      </>
    );
  } else if (state === "offline") {
    content = (
      <>
        <CloudOff className="w-3.5 h-3.5 text-amber-500" />
        <span>Lokal gesichert</span>
      </>
    );
  } else {
    content = (
      <>
        <Cloud className="w-3.5 h-3.5 text-emerald-500" />
        <span>Automatisch gespeichert{timeLabel ? ` · ${timeLabel}` : ""}</span>
      </>
    );
  }
  return (
    <div
      className="shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/60 border border-border rounded-full px-3 py-1.5"
      data-testid="autosave-indicator"
    >
      {content}
    </div>
  );
}
